import React, { Component } from "react";
import NoDataFound from "./NoDataFound";
import { formatEmpDisplayName } from "../utils/empDisplay";

const VIEW_TAB = {
    ROOM: "room",
    STAFF: "staff",
};

const nurseDisplayName = (nurse) =>
    formatEmpDisplayName(nurse?.fullName || nurse?.email, nurse?.empId, "No one yet");

/** Avoid "C Block - 301 - 301" when name already includes the room number */
const formatRoomLabel = (room) => {
    const name = String(room?.patientRoomName || "").trim();
    const id = room?.patientRoomId != null ? String(room.patientRoomId).trim() : "";
    if (!name && !id) return "Room";
    if (!id) return name;
    if (!name) return id;
    if (name === id || name.endsWith(` ${id}`) || name.endsWith(`-${id}`) || name.includes(id)) {
        return name;
    }
    return `${name} · ${id}`;
};

const StatTile = ({ label, value }) => (
    <div className="hierarchy-stat-tile">
        <div className="hierarchy-stat-value">{value}</div>
        <div className="hierarchy-stat-label">{label}</div>
    </div>
);

const RoomWardBlock = ({ ward, index }) => {
    const rooms = ward?.rooms || [];
    return (
        <div className="hierarchy-ward-card">
            <div className="hierarchy-ward-header">
                <div className="hierarchy-ward-index">{index + 1}</div>
                <div className="hierarchy-ward-header-text">
                    <div className="hierarchy-ward-title">{ward?.wardName || "Ward"}</div>
                    <div className="hierarchy-ward-sub">
                        Ward in charge: {nurseDisplayName(ward?.wardNurse)}
                    </div>
                </div>
            </div>

            <div className="hierarchy-list-header">
                <span className="hierarchy-list-header-text">Rooms in this ward</span>
                <span className="hierarchy-list-header-count">{rooms.length}</span>
            </div>

            {rooms.length > 0 ? (
                rooms.map((room, roomIndex) => (
                    <div
                        key={`room-${ward.id}-${room.srmIpRoomId}`}
                        className={`hierarchy-list-row${roomIndex === rooms.length - 1 ? " is-last" : ""}`}
                    >
                        <div className="hierarchy-list-row-title">{formatRoomLabel(room)}</div>
                        <div className="hierarchy-list-row-sub">{nurseDisplayName(room.roomNurse)}</div>
                    </div>
                ))
            ) : (
                <p className="hierarchy-empty-inline">No rooms added in this ward</p>
            )}
        </div>
    );
};

const StaffWardBlock = ({ ward, index }) => {
    const floorNurses = ward?.floorNurses || [];
    const wardNurse = ward?.wardNurse;

    return (
        <div className="hierarchy-ward-card">
            <div className="hierarchy-ward-header">
                <div className="hierarchy-ward-index is-staff">{index + 1}</div>
                <div className="hierarchy-ward-header-text">
                    <div className="hierarchy-ward-title">{ward?.wardName || "Ward"}</div>
                </div>
            </div>

            <div className="hierarchy-person-card">
                <div className="hierarchy-person-role">Ward Nurse</div>
                <div className="hierarchy-person-name">{nurseDisplayName(wardNurse)}</div>
            </div>

            <div className="hierarchy-list-header">
                <span className="hierarchy-list-header-text">Room nurses</span>
                <span className="hierarchy-list-header-count">{floorNurses.length}</span>
            </div>

            {floorNurses.length > 0 ? (
                floorNurses.map((nurse, nurseIndex) => {
                    const nurseRooms = nurse.rooms || [];
                    return (
                        <div
                            key={`floor-${ward.id}-${nurse.userId || nurse.fullName}`}
                            className={`hierarchy-staff-nurse-block${nurseIndex === floorNurses.length - 1 ? " is-last" : ""}`}
                        >
                            <div className="hierarchy-person-role">Room nurse</div>
                            <div className="hierarchy-person-name">{nurseDisplayName(nurse)}</div>
                            {nurseRooms.length > 0 ? (
                                <div className="hierarchy-chip-wrap">
                                    {nurseRooms.map((room) => (
                                        <span
                                            key={`staff-room-${ward.id}-${nurse.userId}-${room.srmIpRoomId}`}
                                            className="hierarchy-chip"
                                        >
                                            {formatRoomLabel(room)}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="hierarchy-empty-inline mb-0">Not assigned</p>
                            )}
                        </div>
                    );
                })
            ) : (
                <p className="hierarchy-empty-inline">No room nurses in this ward</p>
            )}
        </div>
    );
};

class VendorHierarchyTree extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeView: VIEW_TAB.ROOM,
        };
    }

    setActiveView = (key) => {
        this.setState({ activeView: key });
    };

    computeTotals = (services) => {
        let roomCount = 0;
        let roomNurseCount = 0;
        (services || []).forEach((service) => {
            const summary = service.summary || {};
            const wards = service.wards || [];
            roomCount += summary.roomCount ?? wards.reduce(
                (sum, ward) => sum + ((ward.rooms && ward.rooms.length) || 0),
                0
            );
            roomNurseCount += summary.roomNurseCount ?? wards.reduce(
                (sum, ward) => sum + ((ward.floorNurses && ward.floorNurses.length) || 0),
                0
            );
        });
        return { roomCount, roomNurseCount };
    };

    renderServiceCard = (service, isRoomView) => {
        const wards = service?.wards || [];
        const summary = service?.summary || {};
        const wardCount = summary.wardCount ?? wards.length;
        const roomCount = summary.roomCount ?? wards.reduce(
            (sum, ward) => sum + ((ward.rooms && ward.rooms.length) || 0),
            0
        );
        const roomNurseCount = summary.roomNurseCount ?? wards.reduce(
            (sum, ward) => sum + ((ward.floorNurses && ward.floorNurses.length) || 0),
            0
        );
        const headName = nurseDisplayName(service?.headNurse);

        return (
            <div
                className="hierarchy-service-card"
                key={`service-${service?.id || service?.serviceName}`}
            >
                <div className="hierarchy-head-banner">
                    <div className="hierarchy-head-eyebrow">Head Nurse</div>
                    <div className="hierarchy-head-name">{headName}</div>
                    {service?.serviceName ? (
                        <div className="hierarchy-head-dept">
                            Department: {service.serviceName}
                        </div>
                    ) : null}
                </div>

                <div className="hierarchy-stats-row">
                    <StatTile label="Total wards" value={wardCount} />
                    <StatTile label="Total rooms" value={roomCount} />
                    <StatTile label="Room nurses" value={roomNurseCount} />
                </div>

                {wards.length > 0 ? (
                    wards.map((ward, index) => (
                        isRoomView ? (
                            <RoomWardBlock key={`room-ward-${ward.id}`} ward={ward} index={index} />
                        ) : (
                            <StaffWardBlock key={`staff-ward-${ward.id}`} ward={ward} index={index} />
                        )
                    ))
                ) : (
                    <p className="hierarchy-empty-inline">No rooms added in this ward</p>
                )}
            </div>
        );
    };

    renderUnassigned = (unassignedWards, isRoomView) => {
        if (!unassignedWards.length) {
            return null;
        }
        return (
            <div className="hierarchy-service-card hierarchy-unassigned-card">
                <div className="hierarchy-head-banner">
                    <div className="hierarchy-head-eyebrow">Not linked to service</div>
                    <div className="hierarchy-head-name">Wards without a service</div>
                </div>
                {unassignedWards.map((ward, index) => (
                    isRoomView ? (
                        <RoomWardBlock key={`unassigned-room-${ward.id}`} ward={ward} index={index} />
                    ) : (
                        <StaffWardBlock key={`unassigned-staff-${ward.id}`} ward={ward} index={index} />
                    )
                ))}
            </div>
        );
    };

    render() {
        const { treeData } = this.props;
        const { activeView } = this.state;
        const services = treeData?.services || [];
        const unassignedWards = treeData?.unassignedWards || [];
        const updatedAtIst = treeData?.generatedAtIst || "";
        const isRoomView = activeView === VIEW_TAB.ROOM;
        const totals = this.computeTotals(services);

        if (!services.length && !unassignedWards.length) {
            return <NoDataFound />;
        }

        return (
            <div className="hierarchy-tree hierarchy-tree--mobile-parity">
                <div className="hierarchy-view-tabs">
                    <button
                        type="button"
                        className={`hierarchy-view-tab${isRoomView ? " is-active" : ""}`}
                        onClick={() => this.setActiveView(VIEW_TAB.ROOM)}
                    >
                        <span>Room based</span>
                        <span className="hierarchy-view-tab-count">{totals.roomCount}</span>
                    </button>
                    <button
                        type="button"
                        className={`hierarchy-view-tab${!isRoomView ? " is-active" : ""}`}
                        onClick={() => this.setActiveView(VIEW_TAB.STAFF)}
                    >
                        <span>Staff based</span>
                        <span className="hierarchy-view-tab-count">{totals.roomNurseCount}</span>
                    </button>
                </div>

                <div className="hierarchy-toolbar">
                    <p className="hierarchy-flow-hint mb-1">
                        {isRoomView
                            ? "Service → Ward → Room → Room Nurse"
                            : "Service → Ward → Ward Nurse → Room Nurse → Rooms"}
                    </p>
                    {updatedAtIst ? (
                        <p className="hierarchy-updated-text mb-0">Updated at: {updatedAtIst}</p>
                    ) : null}
                </div>

                {services.map((service) => this.renderServiceCard(service, isRoomView))}
                {this.renderUnassigned(unassignedWards, isRoomView)}
            </div>
        );
    }
}

export default VendorHierarchyTree;
